import { ChipsList } from "./ChipsList";
import {
  CountryOption,
  ListSectionProps,
  WhitelistBlacklistManagerProps,
} from "../utils/types";
import CompletedIcon from "../../../../../../../../../../assets/svg/completed-icon";
import CrossIcon from "../../../../../../../../../../assets/svg/cross-icon";
import MultiSelectSearchableDropdown from "../../../../../../../../../../components/MultiSelectSearchableDropdown/MultiSelectSearchableDropdown";
import SearchIcon from "../../../../../../../../../../assets/svg/search-icon";
import { useMemo, useCallback, useEffect } from "react";
import { FormikProps } from "formik";
import { AgentDetailsValues } from "../../../../../utils/types";
import { EditUserDetailsValues } from "../../../../../../EditUserDrawer/utils/types";

const getFieldValues = (
  formik: FormikProps<AgentDetailsValues> | FormikProps<EditUserDetailsValues>,
  fieldName: string
): string[] => {
  return (
    (formik?.values?.[fieldName as keyof AgentDetailsValues] as string[]) || []
  );
};

const createFilteredOptions = (
  transformedOptions: CountryOption[],
  excludeValues: string[]
) => {
  return transformedOptions?.filter(
    (option) => !excludeValues.includes(option?.value)
  );
};

export const WhitelistBlacklistManager = ({
  formik,
  whitelistName,
  blacklistName,
  isFlagIcon,
  Icon,
  iconClassName,
  config,
  transformedOptions,
  isLoading,
}: WhitelistBlacklistManagerProps) => {
  const whitelistValues = getFieldValues(formik, whitelistName);
  const blacklistValues = getFieldValues(formik, blacklistName);

  const whitelistOptions = useMemo(
    () => createFilteredOptions(transformedOptions, blacklistValues),
    [transformedOptions, blacklistValues]
  );

  const blacklistOptions = useMemo(
    () => createFilteredOptions(transformedOptions, whitelistValues),
    [transformedOptions, whitelistValues]
  );

  const createChangeHandler = useCallback(
    (fieldName: string) => (values: string[]) =>
      formik?.setFieldValue(fieldName, values || []),
    [formik]
  );

  const createRemoveHandler = useCallback(
    (fieldName: string) => (itemId: string) => {
      const currentValues = getFieldValues(formik, fieldName);
      formik?.setFieldValue(
        fieldName,
        currentValues.filter((id) => id !== itemId)
      );
    },
    [formik]
  );

  const createClearHandler = useCallback(
    (fieldName: string) => () => formik?.setFieldValue(fieldName, []),
    [formik]
  );

  const handleWhitelistedCountryChange = createChangeHandler(whitelistName);
  const handleWhitelistedCountryRemove = createRemoveHandler(whitelistName);
  const handleWhitelistedCountryClear = createClearHandler(whitelistName);

  const handleBlacklistedCountryChange = createChangeHandler(blacklistName);
  const handleBlacklistedCountryRemove = createRemoveHandler(blacklistName);
  const handleBlacklistedCountryClear = createClearHandler(blacklistName);

  useEffect(() => {
    if (
      whitelistName === "city_whitelist" ||
      blacklistName === "city_blacklist"
    ) {
      whitelistValues?.filter((value) => {
        if (!whitelistOptions?.find((option) => option?.value === value)) {
          handleWhitelistedCountryRemove(value);
        }
      });
      blacklistValues?.filter((value) => {
        if (!blacklistOptions?.find((option) => option?.value === value)) {
          handleBlacklistedCountryRemove(value);
        }
      });
    }
  }, [
    whitelistName,
    blacklistName,
    whitelistValues,
    blacklistValues,
    whitelistOptions,
    blacklistOptions,
    handleWhitelistedCountryRemove,
    handleBlacklistedCountryRemove,
  ]);

  const ListSection = ({
    type,
    values,
    options,
    header,
    onChange,
    onRemove,
    onClear,
    IconComponent,
  }: ListSectionProps) => (
    <div className="flex flex-col gap-y-3">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-x-1">
          <IconComponent
            className={`w-[18px] h-[18px] ${
              type === "whitelist"
                ? "text-verba-green-success dark:text-verba-green-success-dk"
                : "text-verba-red-alert dark:text-verba-red-alert-dk"
            }`}
          />
          <span className="font-semibold text-md text-verba-indigo dark:text-verba-indigo-dk uppercase">
            {header}
          </span>
          {values.length > 0 && (
            <span className="w-[18px] h-[18px] flex items-center justify-center border border-gray-100 dark:border-gray-100-dk text-verba-indigo dark:text-verba-indigo-dk text-xxs rounded-sm">
              {values.length}
            </span>
          )}
        </div>
        {values.length > 0 && (
          <div
            className="text-md font-medium text-verba-blue dark:text-verba-blue-dk cursor-pointer"
            onClick={onClear}
          >
            Clear
          </div>
        )}
      </div>
      <MultiSelectSearchableDropdown
        options={isLoading ? [] : options}
        value={values}
        onChange={onChange}
        placeholder={isLoading ? "Loading..." : config?.placeholder}
        searchPlaceholder={config?.search_placeholder}
        showSearch={true}
        className="w-[264px]"
        placeholderClassName="text-gray-placeholder dark:text-gray-placeholder-dk text-md font-normal"
        searchInputClassName="!border-b-0 text-gray-placeholder dark:text-gray-placeholder-dk text-md gap-1.5 !px-2"
        triggerClassName="!rounded-lg"
        optionsListClassName="!py-0"
        searchIconClassName="w-[18px] h-[18px]"
        optionClassName="text-heading-text dark:text-heading-text-dk text-md font-normal !px-2 border-none "
        selectedOptionClassName="!bg-card-highlight-2 !dark:bg-card-highlight-2-dk"
        inputContainerClassName="!px-2 !py-[9px]"
        triggerIconClassName="!w-[10px] !h-[10px]"
        Icon={SearchIcon}
        iconClassName="w-[18px] h-[18px] text-gray-placeholder dark:text-gray-placeholder-dk"
        showSelectedOptionsChips={false}
      />
      <ChipsList
        ids={values}
        transformedOptions={transformedOptions}
        onRemove={onRemove}
        chipClassName={
          type === "whitelist"
            ? "border-tag-green-stroke dark:border-tag-green-stroke-dk bg-user-management-green-card-bg dark:bg-user-management-green-card-bg-dk"
            : "border-tag-red-stroke dark:border-tag-red-stroke-dk bg-tag-red-bg dark:bg-tag-red-bg-dk"
        }
        isFlagIcon={isFlagIcon}
        Icon={Icon}
        iconClassName={iconClassName}
      />
    </div>
  );

  return (
    <div className="mb-2.5 flex flex-col">
      <ListSection
        type="whitelist"
        values={whitelistValues}
        options={whitelistOptions}
        header={config?.whitelist_header}
        onChange={handleWhitelistedCountryChange}
        onRemove={handleWhitelistedCountryRemove}
        onClear={handleWhitelistedCountryClear}
        IconComponent={CompletedIcon}
      />

      <div className="border-b border-border-gray dark:border-border-gray-dk my-6" />

      <ListSection
        type="blacklist"
        values={blacklistValues}
        options={blacklistOptions}
        header={config?.blacklist_header}
        onChange={handleBlacklistedCountryChange}
        onRemove={handleBlacklistedCountryRemove}
        onClear={handleBlacklistedCountryClear}
        IconComponent={CrossIcon}
      />
    </div>
  );
};
